/* eslint-disable react/jsx-filename-extension */

import React, { useCallback, useState } from 'react';
import { omitBy } from 'lodash-es';
import isEmpty from 'helpers/isEmpty';

const formatGlobalError = (key, label, message) => {
  if (!message) return null;
  if (message.length === 0) return null;

  return {
    key,
    label,
    message,
  };
};

const formatGlobalErrors = (errors, globalAttributes) => (
  globalAttributes
    .map(({ key, label }) => formatGlobalError(key, label, errors[key]))
    .filter(Boolean)
);

const formatErrors = (errors, globalAttributes) => omitBy(
  {
    ...errors,
    global: formatGlobalErrors(errors, globalAttributes),
  },
  isEmpty,
);

const extractGlobalErrors = (response, globalAttributes) => {
  const { status, statusText, data } = response;

  if (status !== 422) {
    return {
      global: [{
        key: status.toString(),
        message: [
          statusText, data,
        ].filter((e) => !!e).join(': '),
      }],
    };
  }

  return formatErrors(data, globalAttributes);
};

const useErrors = (globalAttributes = [], useScroll = false, initialErrors = {}) => {
  const [errors, setErrors] = useState(formatErrors(initialErrors, globalAttributes));

  const clearErrors = () => {
    setErrors({});
  };

  const handleError = useCallback((error) => {
    const { response } = error;
    const newErrors = extractGlobalErrors(response, globalAttributes);

    setErrors(newErrors);

    if (useScroll) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [globalAttributes, useScroll]);

  const errorStatus = useCallback((attributeName) => (
    errors[attributeName] ? 'error' : 'success'
  ), [errors]);

  const errorMessage = useCallback((attributeName) => {
    if (!errors[attributeName]) {
      return null;
    }

    return (
      <>
        {
          errors[attributeName].map((message) => (
            <p key={`${attributeName}${message}`} style={{ margin: 0 }}>{message}</p>
          ))
        }
      </>
    );
  }, [errors]);

  return {
    errors,
    setErrors,
    clearErrors,
    handleError,
    errorStatus,
    errorMessage,
  };
};

export default useErrors;
